import {
  convertQueryCommandToQueryString, IQueryCommandOptions, Null, Or, GreaterThan, GreaterThanOrEqual, Equal, LessThanOrEqual,
} from '@llws/dynamic-router-utils';
import { IPremiumReportingClientCities } from '@llws/lift-entity-interfaces';
import dayjs from 'dayjs';
import { ActionContext } from 'vuex';
import { StoreBaseImplementationV2 } from '@/shared/classes/store-base-implementation-v2';
import { IAction, IStateStrict } from '@/shared/interfaces/store-base';
import { adminRequest } from '@/shared/request-configs/admin-request';
import { mergeQueryCommandOptionsV2 } from '@/shared';

const defaultFilters = {
  alias: 'prcc',
  relations: [
    {
      path: 'city',
      alias: 'gc',
    },
    {
      path: 'gc.premiumReportingCitiesBaseSubscriptions',
      alias: 'prcbs',
    },
    {
      path: 'gc.province',
      alias: 'p',
    },
    {
      path: 'subscription',
      alias: 's',
    },
    {
      path: 's.baseSubscription',
      alias: 'bs',
    },
    {
      path: 'bs.service',
      alias: 'ser',
    },
  ],
};

const adminGatewayResourcePath: string = '/client-api/premium_reporting_client_cities';
const listViewName: string = '';
const showLogging: boolean = false;
const defaultGetListFilters: IQueryCommandOptions = defaultFilters;
const defaultGetSingleFilters: IQueryCommandOptions = {};
const base = new StoreBaseImplementationV2<Partial<IPremiumReportingClientCities>>({
  adminGatewayResourcePath,
  defaultGetSingleFilters,
  defaultGetListFilters,
  listViewName,
  showLogging,
});

type TPremiumReportingClientCitiesState = IStateStrict<IPremiumReportingClientCities> & {
  listLite?: IPremiumReportingClientCities[],
  reportCities?: IPremiumReportingClientCities[],
  reportCitiesLoaded?: boolean,
};

// In-flight `getReportCities` request, shared so concurrent callers (nav + report page on initial load) don't double-fetch
let reportCitiesRequest: Promise<void> | null = null;

/**
 * Drops cities whose subscription has expired, strips accents from city names, and sorts by default priority.
 * @param cities - client cities to normalize
 * @returns active cities, default-priority cities first
 */
const normalizeActiveCities = (cities: IPremiumReportingClientCities[]) => {
  const now = dayjs();
  const nullExpiryStandIn = dayjs().add(1, 'day');

  return cities
    .filter((c) => {
      const expiryDate = c.subscription?.expiryDate ? dayjs(c.subscription.expiryDate) : nullExpiryStandIn;
      return expiryDate.isAfter(now, 'day');
    })
    .map((prc) => ({
      ...prc,
      city: {
        ...prc.city,
        cityName: `${prc.city?.cityName.normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`,
      },
    }))
    .sort((a, b) => (
      (a.cityDefaultPriority ?? 9998) < (b.cityDefaultPriority ?? 9999)
        ? -1
        : 1
    ));
};

export default {
  namespaced: base.namespaced,

  state: {
    ...base.state,
    listLite: [],
    reportCities: [],
    reportCitiesLoaded: false,
  },

  getters: {
    ...base.getters,

    /**
     * Returns state.list as a unique list of cities, starting by removing any cities with inactive subscriptions.
     * @param state
     * @returns
     */
    defaultCitySelectList: (state: IStateStrict<IPremiumReportingClientCities>) => {
      if (!state.list?.data) {
        return [];
      }

      const now = dayjs();
      const nullExpiryStandIn = dayjs().add(1, 'day');

      return state.list?.data.reduce<IPremiumReportingClientCities[]>((acc, cur) => {
        const expiryDate = (
          cur.subscription?.expiryDate
            ? dayjs(cur.subscription.expiryDate)
            : nullExpiryStandIn
        );

        if (expiryDate.isAfter(now, 'day') && !acc.some((city) => city.cityId === cur.cityId)) {
          acc.push(cur);
        }

        return acc;
      }, []);
    },

    listNormalized: (state: IStateStrict<IPremiumReportingClientCities>) => {
      if (!state.list?.data) {
        return [];
      }

      return normalizeActiveCities(state.list.data);
    },

    /**
     * Active subscribed cities used by the Market Insights reports and nav. Kept separate from `list`, which the
     * settings pages overwrite with their own paginated/filtered queries.
     */
    reportCitiesNormalized: (state: TPremiumReportingClientCitiesState) => normalizeActiveCities(state.reportCities ?? []),

    listLite: (
      state: IStateStrict<IPremiumReportingClientCities> & { listLite?: IPremiumReportingClientCities[] },
    ) => state.listLite ?? [],
  },

  mutations: {
    ...base.mutations,

    setListLite: (
      state: IStateStrict<IPremiumReportingClientCities> & { listLite?: IPremiumReportingClientCities[] },
      payload: IPremiumReportingClientCities[],
    ) => {
      state.listLite = payload;
    },

    setReportCities: (state: TPremiumReportingClientCitiesState, payload: IPremiumReportingClientCities[]) => {
      state.reportCities = payload;
      state.reportCitiesLoaded = true;
    },

    resetReportCities: (state: TPremiumReportingClientCitiesState) => {
      state.reportCities = [];
      state.reportCitiesLoaded = false;
    },
  },

  actions: {
    ...base.actions,

    getLiteList: async (ctx: IAction<IStateStrict<typeof base.state>>) => {
      const now = dayjs().format('YYYY-MM-DD');

      const query: IQueryCommandOptions = {
        alias: 'prcc',
        select: [
          'prcc.id',
          'prcc.clientId',
          'prcc.subscriptionId',
          'prcc.cityId',
        ],
        relations: [
          { path: 'prcc.subscription', alias: 'sub' },
        ],
        where: [
          {
            condition: 'and',
            whereCommands: [
              Or(Null('sub.expiryDate')),
              Or(GreaterThan('sub.expiryDate', now)),
            ],
          },
        ],
        orderBy: [
          { field: 'sub.startDate', direction: 'asc' },
        ],
        limit: 'false',
      };

      const res = await adminRequest.get<IPremiumReportingClientCities>(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(query)}`);
      ctx.commit('setListLite', res.data ?? []);
    },

    /**
     * Fetches the client's currently active city subscriptions into `reportCities`, unless they're already loaded.
     * Commit `resetReportCities` to force a refetch (e.g. after default cities change). Concurrent calls share one request.
     * @param ctx - action context
     * @param payload.clientId - client whose cities to fetch
     */
    getReportCities: async (
      ctx: IAction<TPremiumReportingClientCitiesState>,
      payload: { clientId?: number | null },
    ) => {
      if (ctx.state.reportCitiesLoaded || !payload.clientId) {
        return;
      }

      if (reportCitiesRequest) {
        await reportCitiesRequest;
        return;
      }

      const now = dayjs().format('YYYY-MM-DD');

      const query = mergeQueryCommandOptionsV2([
        defaultGetListFilters,
        {
          where: [
            Equal('clientId', `${payload.clientId}`),
            LessThanOrEqual('s.startDate', now),
            {
              condition: 'and',
              whereCommands: [
                Or(GreaterThanOrEqual('s.expiryDate', now)),
                Or(Null('s.expiryDate')),
              ],
            },
          ],
          limit: 9999,
        },
      ]);

      reportCitiesRequest = (async () => {
        const res = await adminRequest.get<IPremiumReportingClientCities[]>(`${adminGatewayResourcePath}${convertQueryCommandToQueryString(query)}`);
        ctx.commit('setReportCities', res.data ?? []);
      })();

      try {
        await reportCitiesRequest;
      } finally {
        reportCitiesRequest = null;
      }
    },
  },
};
